import {
  UserPoints,
  PointsTransaction,
  PointsTransactionType,
  OrderInfo,
  PointProduct,
  CreateTransactionDto,
  PointsReferenceType,
} from "common/models/types";
import axiosInstance from "./axiosInstance";
import { log, LogLevel } from "utils/LogUtil";

// API 응답 기본 인터페이스
export interface ApiResponse<T = any> {
  error: number; // 0은 성공
  message?: string;
  data?: T;
  meta?: {
    totalItems: number;
    count: number;
    hasMore: boolean;
    offset: number;
    limit: number;
  };
}

// 포인트 잔액 조회 응답 인터페이스
interface PointsBalanceResponse {
  currentPoints: number;
  totalEarnedPoints: number;
  totalUsedPoints: number;
  hasFirstPurchase: boolean;
}

// 포인트 상품 응답 인터페이스
interface PointProductResponse {
  products: PointProduct[];
  pointRate: number;
  aiAnalysisCost: number;
}

// 포인트 잔액 조회
export const fetchUserPoints = async (): Promise<UserPoints> => {
  try {
    const response = await axiosInstance.get<ApiResponse<PointsBalanceResponse>>(
      "/api/v1/points/balance"
    );
    return {
      userId: "", // 서버에서 제공하지 않으므로 빈 문자열
      currentPoints: response.data.data?.currentPoints || 0,
      totalEarnedPoints: response.data.data?.totalEarnedPoints || 0,
      totalUsedPoints: response.data.data?.totalUsedPoints || 0,
      hasFirstPurchase: response.data.data?.hasFirstPurchase || false,
    };
  } catch (error) {
    log(LogLevel.ERROR, "포인트 잔액 조회 실패:", error);
    throw error;
  }
};

// 포인트 거래 내역 조회
export const fetchPointsTransactions = async (params?: {
  start?: number;
  count?: number;
  type?: string; // PointsTransactionType enum 값
  startDate?: string;
  endDate?: string;
}): Promise<PointsTransaction[]> => {
  try {
    if (params.endDate) {
      params.endDate = params.endDate + "T23:59:59";
    }
    const response = await axiosInstance.get<ApiResponse<PointsTransaction[]>>(
      "/api/v1/points/transactions",
      {
        params,
      }
    );
    return response.data.data || [];
  } catch (error) {
    log(LogLevel.ERROR, "포인트 거래 내역 조회 실패:", error);
    throw error;
  }
};

// 특정 사용자의 포인트 거래 내역 조회 (관리자용)
export const fetchPointsTransactionsByUserId = async (
  userId: string,
  params?: {
    page?: number;
    limit?: number;
    type?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<PointsTransaction[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<PointsTransaction[]>>(
      "/api/v1/points/admin/transactions",
      { params: { userId, ...params } }
    );
    return response.data.data || [];
  } catch (error) {
    log(LogLevel.ERROR, "사용자 포인트 거래 내역 조회 실패:", error);
    throw error;
  }
};

// 구매 내역 조회
export const fetchOrders = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}): Promise<OrderInfo[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<OrderInfo[]>>("/api/v1/points/orders", {
      params,
    });
    return response.data.data || [];
  } catch (error) {
    log(LogLevel.ERROR, "구매 내역 조회 실패:", error);
    throw error;
  }
};

// 포인트 상품 목록 조회
export const fetchPointProducts = async (): Promise<PointProductResponse> => {
  try {
    const response = await axiosInstance.get<PointProductResponse>("/api/v1/points/products");
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "포인트 상품 목록 조회 실패:", error);
    throw error;
  }
};

// 포인트 구매 요청
export const createPointPurchase = async (data: Partial<OrderInfo>): Promise<OrderInfo> => {
  try {
    const response = await axiosInstance.post<ApiResponse<OrderInfo>>(
      "/api/v1/points/orders",
      data
    );
    return response.data.data;
  } catch (error) {
    log(LogLevel.ERROR, "포인트 구매 요청 실패:", error);
    throw error;
  }
};

// 관리자 포인트 지급
export const adminGrantPoints = async (
  data: Partial<PointsTransaction>
): Promise<PointsTransaction> => {
  try {
    const response = await axiosInstance.post<PointsTransaction>("/api/v1/points/admin/grant", {
      ...data,
      transactionType: data.transactionType || PointsTransactionType.ADMIN_GRANT,
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "관리자 포인트 지급 실패:", error);
    throw error;
  }
};

// 주문 취소
export const cancelOrder = async (orderId: number): Promise<OrderInfo> => {
  try {
    const response = await axiosInstance.put<OrderInfo>(`/api/v1/points/orders/${orderId}/cancel`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "주문 취소 실패:", error);
    throw error;
  }
};

// 주문 환불
export const refundOrder = async (orderId: number): Promise<OrderInfo> => {
  try {
    const response = await axiosInstance.put<OrderInfo>(`/api/v1/points/orders/${orderId}/refund`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "주문 환불 실패:", error);
    throw error;
  }
};

// 주문 상세 조회
export const fetchOrderById = async (orderId: number): Promise<OrderInfo> => {
  try {
    const response = await axiosInstance.get<OrderInfo>(`/api/v1/points/orders/${orderId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "주문 상세 조회 실패:", error);
    throw error;
  }
};

// 주문 정보 업데이트 (결제 완료 후)
export const updatePointOrder = async (
  data: Partial<OrderInfo> & {
    id: number;
  }
): Promise<OrderInfo> => {
  try {
    const response = await axiosInstance.put<OrderInfo>(`/api/v1/points/orders/${data.id}`, data);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "주문 정보 업데이트 실패:", error);
    throw error;
  }
};

// 결제 검증 API
export const verifyPayment = async (data: {
  orderId: number;
  impUid: string;
  merchantUid: string;
}): Promise<void> => {
  try {
    await axiosInstance.post("/api/v1/points/payment/verify", data);
  } catch (error) {
    log(LogLevel.ERROR, "결제 검증 실패:", error);
    throw error;
  }
};

// 결제 완료 처리 API
export const completePayment = async (data: {
  orderId: number;
  impUid: string;
  merchantUid: string;
}): Promise<OrderInfo> => {
  try {
    const response = await axiosInstance.post<OrderInfo>("/api/v1/points/payment/complete", data);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "결제 완료 처리 실패:", error);
    throw error;
  }
};

// AI 분석을 위한 포인트 사용
export const deductPointsForAIAnalysis = async (data: {
  userId: string;
  reviewId: number;
  points: number;
  description: string;
}): Promise<PointsTransaction> => {
  try {
    const transactionData: CreateTransactionDto = {
      userId: data.userId,
      points: -data.points, // 차감이므로 음수
      transactionType: PointsTransactionType.AI_ANALYSIS,
      description: data.description,
      referenceId: data.reviewId.toString(),
      referenceType: PointsReferenceType.AI_REVIEW,
    };

    const response = await axiosInstance.post<ApiResponse<PointsTransaction>>(
      "/api/v1/points/transactions",
      transactionData
    );

    if (response.data.error) {
      throw new Error(response.data.message || "포인트 사용 실패");
    }

    return response.data.data;
  } catch (error) {
    log(LogLevel.ERROR, "AI 분석 포인트 사용 실패:", error);
    throw error;
  }
};

// AI Review 포인트 환불 (관리자 전용)
export const refundAIReviewPoints = async (data: {
  reviewId: number;
  userId: string;
}): Promise<ApiResponse<PointsTransaction>> => {
  try {
    const response = await axiosInstance.post<ApiResponse<PointsTransaction>>(
      "/api/v1/points/admin/refund-ai-review",
      {
        aiReviewId: data.reviewId,
        userId: data.userId,
      }
    );

    if (response.data.error) {
      throw new Error(response.data.message || "포인트 환불 처리 실패");
    }

    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, "AI Review 포인트 환불 실패:", error);
    throw error;
  }
};
